import React, { useEffect, useRef } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { Checkbox, FormControlLabel, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { SignupRequestDTO } from '../../../types/authentication.dto';

type GooglePlaceResult = { formatted_address?: string };

type GoogleAutocomplete = {
  addListener: (eventName: string, handler: () => void) => void;
  getPlace: () => GooglePlaceResult;
};

declare global {
  interface Window {
    google?: {
      maps?: {
        places?: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: Record<string, unknown>
          ) => GoogleAutocomplete;
        };
        event?: {
          clearInstanceListeners: (instance: unknown) => void;
        };
      };
    };
  }
}

type Props = {
  control: Control<SignupRequestDTO>;
  watch: UseFormWatch<SignupRequestDTO>;
  setValue: UseFormSetValue<SignupRequestDTO>;
};

const useStyles = makeStyles((theme) => ({
  root: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  checkbox: {
    color: theme.palette.client.primary.main,
    '&.Mui-checked': { color: theme.palette.client.primary.main },
  },
}));

function isGooglePlacesAvailable(): boolean {
  return typeof window !== 'undefined' && Boolean(window.google?.maps?.places);
}

const DeliveryStep: React.FC<Props> = ({ control, setValue }) => {
  const classes = useStyles();
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const placesAvailable = isGooglePlacesAvailable();

  useEffect(() => {
    const places = window.google?.maps?.places;

    if (!placesAvailable || !addressInputRef.current || !places) return undefined;

    const autocomplete = new places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: ['fr'] },
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (place.formatted_address) {
        setValue('address', place.formatted_address, { shouldValidate: true, shouldDirty: true });
      }
    });

    return () => {
      window.google?.maps?.event?.clearInstanceListeners(autocomplete);
    };
  }, [placesAvailable, setValue]);

  return (
    <div className={classes.root}>
      <Controller
        name="phoneNumber"
        control={control}
        rules={{ required: 'Veuillez saisir votre numéro de téléphone' }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            label="Téléphone"
            type="tel"
            autoComplete="tel"
            variant="outlined"
            fullWidth
            error={Boolean(error)}
            helperText={error?.message}
            inputProps={{ name: 'phoneNumber', 'aria-invalid': Boolean(error) }}
          />
        )}
      />

      <Controller
        name="address"
        control={control}
        rules={{ required: 'Veuillez saisir votre adresse' }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            inputRef={(node: HTMLInputElement | null) => {
              field.ref(node);
              addressInputRef.current = node;
            }}
            label="Adresse"
            autoComplete="address-line1"
            variant="outlined"
            fullWidth
            error={Boolean(error)}
            helperText={error?.message ?? (placesAvailable ? undefined : 'Saisissez votre adresse manuellement')}
            inputProps={{ name: 'address', 'aria-invalid': Boolean(error) }}
          />
        )}
      />

      <Controller
        name="information"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            label="Complément d'adresse"
            autoComplete="address-line2"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            inputProps={{ name: 'information' }}
          />
        )}
      />

      <Controller
        name="newsLetter"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                className={classes.checkbox}
                checked={field.value}
                onChange={(event) => field.onChange(event.target.checked)}
                inputProps={{ 'aria-label': 'Recevoir la newsletter' } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label="Je souhaite recevoir la newsletter"
          />
        )}
      />
    </div>
  );
};

export default DeliveryStep;
